import { yupResolver } from '@hookform/resolvers/yup';
import { CircularProgress, Grid, Link, MenuItem, Typography, useMediaQuery } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/system';
import { useGA4React } from 'ga-4-react';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { objectToArray } from '../../libs/helpers';
import SaveProcess from '../dialogs/saveProcess';
import PrimaryButton from '../ui/buttons/primaryButton';
import Form from '../ui/forms';
import FileDropzone from '../ui/forms/fields/fileDropzone';
import PrimarySelect from '../ui/forms/fields/select';
import PasswordInput from '../ui/forms/fields/passwordField';
import { Report } from '../../libs/loggers';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    border: 'dashed 2px',
    borderColor: theme.palette.primary.main,
    cursor: 'pointer',
  },
  uploadedHeader: {
    color: theme.palette.primary.main,
    fontSize: '1em',
  },
}));

export default function RequiredDocumentation({
  extendedForm,
  data,
  submitionData,
  onNext,
  onBack,
  stepSlug,
  openSave,
  onSaveAndExit,
}) {
  let ga4React = useGA4React();

  const altBanking = submitionData?.mopAlternativeBankingDetails;
  const taxDocRequired = extendedForm && submitionData?.FATCAconfirm !== 'NotRequired';
  const submitionFiles = submitionData.submitionFiles;
  const loading = useSelector((state) => state.submition.submition?.loading);
  const userId = useSelector((state) => state.submition.data?.userId);
  const page = window.location.pathname.replace(/^\/|\/$/g, '');
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const submitionId = useSelector((state) => state.submition.submition?.submitionId);
  const formStepper = useSelector((state) => state.formStepper.value);

  const actingOnBehalf = submitionData?.aobo;

  const requiredFile = (message) =>
    yup.mixed().test('required-file', message, (value) => {
      if (!value) return false;

      // uploaded document exists
      if (value.uuid) return true;
      if (value._id) return true;
      if (value.awsFilePath) return true;
      if (value.fileName) return true;

      return false;
    });

  const validationSchema = yup.object().shape({
    stepSlug: yup.string().required(),
    idType: yup.string().required('Select a ID type to upload your ID Document'),

    fileIDDocument: yup.mixed().when('idType', {
      is: (value) => value === 'document' || value === 'passport',
      then: () => requiredFile('Please upload your ID Book or Passport.'),
      otherwise: () => yup.mixed().nullable(),
    }),

    fileIDCardFront: yup.mixed().when('idType', {
      is: 'card',
      then: () => requiredFile('Please upload the front of your ID Card.'),
      otherwise: () => yup.mixed().nullable(),
    }),

    fileIDCardBack: yup.mixed().when('idType', {
      is: 'card',
      then: () => requiredFile('Please upload the back of your ID Card.'),
      otherwise: () => yup.mixed().nullable(),
    }),

    fileProofOfAddress: requiredFile('Proof of address is required'),

    fileProofOfBankAccount: requiredFile('Proof of banking is required'),

    fileProofOfAltBankAccount: yup.mixed().when('$altBanking', {
      is: true,
      then: () => requiredFile('Proof of Third Party banking is required'),
      otherwise: () => yup.mixed().nullable(),
    }),

    idTypeThirdParty: yup.string().when('$altBanking', {
      is: true,
      then: (schema) => schema.required('Please select the ID Type for the third party'),
      otherwise: (schema) => schema.nullable(),
    }),

    fileIDDocumentThirdParty: yup.mixed().when(['$altBanking', 'idTypeThirdParty'], {
      is: (altBanking, idType) => altBanking && (idType === 'document' || idType === 'passport'),
      then: () => requiredFile('ID Document for Third Party is required'),
      otherwise: () => yup.mixed().nullable(),
    }),

    fileIDDocumentThirdPartyFront: yup.mixed().when(['$altBanking', 'idTypeThirdParty'], {
      is: (altBanking, idType) => altBanking && idType === 'card',
      then: () => requiredFile('Front side of ID Card is required for the third party'),
      otherwise: () => yup.mixed().nullable(),
    }),

    fileIDDocumentThirdPartyBack: yup.mixed().when(['$altBanking', 'idTypeThirdParty'], {
      is: (altBanking, idType) => altBanking && idType === 'card',
      then: () => requiredFile('Back side of ID Card is required for the third party'),
      otherwise: () => yup.mixed().nullable(),
    }),

    fileProofOfAddressThirdParty: yup.mixed().when('$altBanking', {
      is: true,
      then: () => requiredFile('Proof of address for Third Party is required'),
      otherwise: () => yup.mixed().nullable(),
    }),

    fileTaxDocument: yup.mixed().when('$taxDocRequired', {
      is: true,
      then: () => requiredFile('Proof of tax income number.'),
      otherwise: () => yup.mixed().nullable(),
    }),

    filePowerOfAttorney: yup.mixed().nullable(),

    fileUnabridgedBirthCertificate: yup.mixed().when('$actingOnBehalf', {
      is: true,
      then: () => requiredFile('Proof of Unabridged birth certificate is required'),
      otherwise: () => yup.mixed().nullable(),
    }),
  });

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = methods;

  const handleNext = async () => {
    ga4React.event('complete_form_step', `${stepSlug}`, 'investement_submition');
    setValue('completeProcess', false, { shouldValidate: true });
    console.log(getValues());
    handleSubmit(onSubmit)();
  };

  const handleSaveAndExit = (userId) => {
    const data = getValues();
    const newSubmition = { ...submitionData, submitionFiles: data };
    onSaveAndExit(newSubmition, userId);
  };

  const handleOpenSave = () => {
    const data = getValues();
    const newSubmition = { ...submitionData, submitionFiles: data };
    openSave(newSubmition);
  };

  const onSubmit = (data) => {
    const newSubmition = { ...submitionData, submitionFiles: data };
    onNext(newSubmition);
  };

  React.useEffect(() => {
  setValue('stepSlug', stepSlug, { shouldValidate: true });
}, [stepSlug, setValue]);

  React.useEffect(() => {
    if (submitionFiles) {
      console.log('submitionFiles:', submitionFiles.passwordForFileIDDocument);
    }
  }, [submitionFiles]);

  React.useEffect(() => {
    const eventInfo = {
      action: 'view',
      category: 'step4_formstep_view',
      description: 'viewed a form step in step 4',
      value: {
        currentPage: page,
        stepSlug,
        activeFormStep: formStepper?.step,
      },
    };

    Report(eventInfo, userId, submitionId);
  }, []);

  React.useEffect(() => {
    trigger(['fileIDDocument', 'fileIDCardFront', 'fileIDCardBack']);
  }, [watch('idType'), trigger]);

  const NavButtons = (props) => (
    <>
      <Box display="flex" justifyContent={'flex-end'}>
        <PrimaryButton disabled={props.isFirstStep} onClick={() => onBack()} sx={{ mt: 1, mr: 1 }}>
          Back
        </PrimaryButton>
        <PrimaryButton
          onClick={() => handleNext()}
          disabled={loading}
          sx={{
            mt: 1,
            mr: 1,
            '&.Mui-disabled': {
              background: '#CC6323',
              color: '#fff',
            },
          }}
        >
          {userId ? 'Save & Continue' : 'Continue'}{' '}
          {loading && <CircularProgress sx={{ ml: 1 }} size={isMobile ? 15 : 20} color="inherit" />}
        </PrimaryButton>
        <PrimaryButton variant="outlined" onClick={() => handleSaveAndExit(userId)} sx={{ mt: 1 }}>
          {'Save & Exit'}
        </PrimaryButton>
      </Box>
      {!userId && (
        <Box display="flex" justifyContent={'flex-end'} marginTop={1}>
          <Typography variant="body1">
            {' '}
            Your progress is not being saved. Click{' '}
            <Link
              sx={{ cursor: 'pointer' }}
              variant="body1"
              underline="always"
              onClick={() => {
                handleOpenSave();
              }}
            >
              here
            </Link>{' '}
            to save.
          </Typography>
        </Box>
      )}
    </>
  );
  return (
    <>
      <FormProvider {...methods}>
        <Form>
          {actingOnBehalf ? (
            <>
              <Typography variant="h5" color="primary" sx={{ mt: 2, mb: 2 }}>
                Investor's Documentation
              </Typography>

              <Box ml={3}>
                <FileDropzone
                  info={''}
                  name="fileUnabridgedBirthCertificate"
                  label="Unabridged Birth Certificate"
                  defaultValue={submitionFiles && submitionFiles.fileUnabridgedBirthCertificate}
                  passwordField={
                    <PasswordInput
                      name={`fileUnabridgedBirthCertificate.password`}
                      control={control}
                      label="Password (optional)"
                      defaultValue={submitionFiles?.fileUnabridgedBirthCertificate?.password}
                    />
                  }
                  category="birthCertificate"
                />

                <FileDropzone
                  info={'Not older than 3 months'}
                  name="fileProofOfAddress"
                  label="Proof of Address"
                  defaultValue={submitionFiles && submitionFiles.fileProofOfAddress}
                  category="proofOfAddressDocument"
                  tooltip={
                    <Typography>
                      Acceptable proof of address documents: utility bill (
                      <i>not older than 3 months</i>), bank statement (
                      <i>not older than 3 months</i>), rates account (<i>not older than 3 months</i>
                      ), SARS tax invoice/return with address (<i>not older than 12 months</i>) or
                      valid lease agreement.
                    </Typography>
                  }
                  passwordField={
                    <PasswordInput
                      name={`fileProofOfAddress.password`}
                      control={control}
                      label="Password (optional)"
                      defaultValue={submitionFiles?.fileProofOfAddress?.password}
                    />
                  }
                />

                <FileDropzone
                  info="Not older than 3 months"
                  name="fileProofOfBankAccount"
                  label="Proof of Bank Account"
                  defaultValue={submitionFiles?.fileProofOfBankAccount?.file}
                  category="proofOfBankDocument"
                  tooltip={
                    <Typography>
                      Acceptable proof of bank documents: bank statement (
                      <i>not older than 3 months</i>) or stamped confirmation letter from the bank
                      on a bank letterhead (<i>not older than 3 months</i>)
                    </Typography>
                  }
                  passwordField={
                    <PasswordInput
                      name={`fileProofOfBankAccount.password`}
                      control={control}
                      label="Password (optional)"
                      defaultValue={submitionFiles?.fileProofOfBankAccount?.password}
                    />
                  }
                />
              </Box>
              {altBanking && (
                <>
                  <Typography variant="h5" color="primary" sx={{ mt: 2, mb: 2 }}>
                    Third Party Bank Details
                  </Typography>

                  <Box ml={3}>
                    <Typography sx={{ mt: 1 }} variant="h5">
                      Proof of Identity (Third Party)
                    </Typography>

                    <PrimarySelect
                      name="idTypeThirdParty"
                      control={control}
                      label="Select ID Type"
                      defaultValue={submitionFiles && submitionFiles.idTypeThirdParty}
                      required
                      error={!!errors.idTypeThirdParty}
                      helperText={errors.idTypeThirdParty?.message}
                    >
                      <MenuItem value="document">ID Book</MenuItem>
                      <MenuItem value="card">ID Card</MenuItem>
                      <MenuItem value="passport">Passport</MenuItem>
                    </PrimarySelect>

                    <Typography sx={{ mb: 2 }}>
                      You may scan the document or take a photograph and upload. Please ensure the
                      quality is clear and legible. E.g. South African ID document, Passport, South
                      African ID card. (Please note that if you are uploading a copy/photograph of
                      your South African ID card, we require both the back and front)
                    </Typography>

                    {(watch('idTypeThirdParty') === 'document' ||
                      watch('idTypeThirdParty') === 'passport') && (
                      <FileDropzone
                        name="fileIDDocumentThirdParty"
                        label={watch('idTypeThirdParty') === 'document' ? 'ID Book' : 'Passport'}
                        defaultValue={submitionFiles && submitionFiles.fileIDDocumentThirdParty}
                        category="idDocumentThirdParty"
                        passwordField={
                          <PasswordInput
                            name={`fileIDDocumentThirdParty.password`}
                            control={control}
                            label="Password (optional)"
                            defaultValue={submitionFiles?.fileIDDocumentThirdParty?.password}
                          />
                        }
                      />
                    )}

                    {watch('idTypeThirdParty') === 'card' && (
                      <>
                        <FileDropzone
                          name="fileIDDocumentThirdPartyFront"
                          label="ID Card - Front"
                          defaultValue={
                            submitionFiles && submitionFiles.fileIDDocumentThirdPartyFront
                          }
                          category="idDocumentThirdPartyFront"
                          passwordField={
                            <PasswordInput
                              name={`fileIDDocumentThirdPartyFront.password`}
                              control={control}
                              label="Password (optional)"
                              defaultValue={submitionFiles?.fileIDDocumentThirdPartyFront?.password}
                            />
                          }
                        />
                        <FileDropzone
                          name="fileIDDocumentThirdPartyBack"
                          label="ID Card - Back"
                          defaultValue={
                            submitionFiles && submitionFiles.fileIDDocumentThirdPartyBack
                          }
                          category="idDocumentThirdPartyBack"
                          passwordField={
                            <PasswordInput
                              name={`fileIDDocumentThirdPartyBack.password`}
                              control={control}
                              label="Password (optional)"
                              defaultValue={submitionFiles?.fileIDDocumentThirdPartyBack?.password}
                            />
                          }
                        />
                      </>
                    )}
                  </Box>

                  <Box ml={3}>
                    <FileDropzone
                      info={'Not older than 3 months'}
                      name="fileProofOfAltBankAccount"
                      label="Proof of Third Party Bank Account"
                      defaultValue={submitionFiles && submitionFiles.fileProofOfAltBankAccount}
                      category="proofOfAlternativeBankDocument"
                      tooltip={
                        <Typography>
                          Acceptable proof of bank documents: bank statement (
                          <i>not older than 3 months</i>) or stamped confirmation letter from the
                          bank on a bank letterhead (<i>not older than 3 months</i>)
                        </Typography>
                      }
                      passwordField={
                        <PasswordInput
                          name={`fileProofOfAltBankAccount.password`}
                          control={control}
                          label="Password (optional)"
                          defaultValue={submitionFiles?.fileProofOfAltBankAccount?.password}
                        />
                      }
                    />

                    <FileDropzone
                      name="fileProofOfAddressThirdParty"
                      label="Proof of Address of Third Party"
                      defaultValue={submitionFiles && submitionFiles.fileProofOfAddressThirdParty}
                      category="proofOfAddressThirdParty"
                      passwordField={
                        <PasswordInput
                          name={`fileProofOfAddressThirdParty.password`}
                          control={control}
                          label="Password (optional)"
                          defaultValue={submitionFiles?.fileProofOfAddressThirdParty?.password}
                        />
                      }
                    />
                  </Box>
                </>
              )}

              <Typography variant="h5" color="primary" sx={{ mt: 2, mb: 2 }}>
                Authorised Party's Documentation
              </Typography>

              <Box ml={3}>
                <Typography sx={{ mt: 1 }} variant={'h5'}>
                  {'Proof of Identity'}
                </Typography>
                <PrimarySelect
                  name="idType"
                  control={control}
                  label="Select ID Type"
                  error={!!errors.idType}
                  helperText={errors.idType?.message}
                  defaultValue={submitionFiles && submitionFiles.idType}
                  required
                >
                  <MenuItem value="document">ID Book</MenuItem>
                  <MenuItem value="card">ID Card</MenuItem>
                  <MenuItem value="passport">Passport</MenuItem>
                </PrimarySelect>
                <Typography>
                  You may scan the document or take a photograph and upload. Please ensure the
                  quality is clear and legible. EG. South African ID document, Passport, South
                  African ID card. (Please note that if you are uploading a copy/photograph of you
                  South African ID card, we require both the back and front)
                </Typography>

                {watch('idType') === 'document' || watch('idType') === 'passport' ? (
                  <FileDropzone
                    name="fileIDDocument"
                    label={watch('idType') === 'document' ? 'ID Book' : 'Passport'}
                    defaultValue={submitionFiles && submitionFiles.fileIDDocument}
                    category="idDocument"
                    passwordField={
                      <PasswordInput
                        name={`fileIDDocument.password`}
                        control={control}
                        label="Password (optional)"
                        defaultValue={submitionFiles?.fileIDDocument?.password}
                      />
                    }
                  />
                ) : watch('idType') === 'card' ? (
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <FileDropzone
                        name="fileIDCardFront"
                        label="ID Card Front"
                        defaultValue={submitionFiles && submitionFiles.fileIDCardFront}
                        category="idCardFrontDocument"
                        passwordField={
                          <PasswordInput
                            name={`fileIDCardFront.password`}
                            control={control}
                            label="Password (optional)"
                            defaultValue={submitionFiles?.fileIDCardFront?.password}
                          />
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FileDropzone
                        name="fileIDCardBack"
                        label="ID Card Back"
                        defaultValue={submitionFiles && submitionFiles.fileIDCardBack}
                        category="idCardBackDocument"
                        passwordField={
                          <PasswordInput
                            name={`fileIDCardBack.password`}
                            control={control}
                            label="Password (optional)"
                            defaultValue={submitionFiles?.fileIDCardBack?.password}
                          />
                        }
                      />
                    </Grid>
                  </Grid>
                ) : null}

                <FileDropzone
                  name="filePowerOfAttorney"
                  label="Power of Attorney / Letter of Authority (where applicable)"
                  defaultValue={submitionFiles && submitionFiles.filePowerOfAttorney}
                  category="powerOfAttorney"
                  passwordField={
                    <PasswordInput
                      name={`filePowerOfAttorney.password`}
                      control={control}
                      label="Password (optional)"
                      defaultValue={submitionFiles?.filePowerOfAttorney?.password}
                    />
                  }
                />
              </Box>
            </>
          ) : (
            <>
              <Typography sx={{ mt: 1 }} variant={'h5'}>
                {'Proof of Identity'}
              </Typography>
              <PrimarySelect
                name="idType"
                control={control}
                label="Select ID Type"
                error={!!errors.idType}
                helperText={errors.idType?.message}
                defaultValue={submitionFiles && submitionFiles.idType}
                required
              >
                <MenuItem value="document">ID Book</MenuItem>
                <MenuItem value="card">ID Card</MenuItem>
                <MenuItem value="passport">Passport</MenuItem>
              </PrimarySelect>
              <Typography>
                You may scan the document or take a photograph and upload. Please ensure the quality
                is clear and legible. EG. South African ID document, Passport, South African ID
                card. (Please note that if you are uploading a copy/photograph of you South African
                ID card, we require both the back and front)
              </Typography>

              {watch('idType') === 'document' || watch('idType') === 'passport' ? (
                <FileDropzone
                  name="fileIDDocument"
                  label={watch('idType') === 'document' ? 'ID Book' : 'Passport'}
                  defaultValue={submitionFiles && submitionFiles.fileIDDocument}
                  category="idDocument"
                  passwordField={
                    <PasswordInput
                      name={`fileIDDocument.password`}
                      control={control}
                      label="Password (optional)"
                      defaultValue={submitionFiles?.fileIDDocument?.password}
                    />
                  }
                />
              ) : watch('idType') === 'card' ? (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FileDropzone
                      name="fileIDCardFront"
                      label="ID Card Front"
                      defaultValue={submitionFiles && submitionFiles.fileIDCardFront}
                      category="idCardFrontDocument"
                      passwordField={
                        <PasswordInput
                          name={`fileIDCardFront.password`}
                          control={control}
                          label="Password (optional)"
                          defaultValue={submitionFiles?.fileIDCardFront?.password}
                        />
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FileDropzone
                      name="fileIDCardBack"
                      label="ID Card Back"
                      defaultValue={submitionFiles && submitionFiles.fileIDCardBack}
                      category="idCardBackDocument"
                      passwordField={
                        <PasswordInput
                          name={`fileIDCardBack.password`}
                          control={control}
                          label="Password (optional)"
                          defaultValue={submitionFiles?.fileIDCardBack?.password}
                        />
                      }
                    />
                  </Grid>
                </Grid>
              ) : null}

              <FileDropzone
                info={'Not older than 3 months'}
                name="fileProofOfAddress"
                label="Proof of Address"
                defaultValue={submitionFiles && submitionFiles.fileProofOfAddress}
                category="proofOfAddressDocument"
                tooltip={
                  <Typography>
                    Acceptable proof of address documents: utility bill (
                    <i>not older than 3 months</i>), bank statement (<i>not older than 3 months</i>
                    ), rates account (<i>not older than 3 months</i>), SARS tax invoice/return with
                    address (<i>not older than 12 months</i>) or valid lease agreement.
                  </Typography>
                }
                passwordField={
                  <PasswordInput
                    name={`fileProofOfAddress.password`}
                    control={control}
                    label="Password (optional)"
                    defaultValue={submitionFiles?.fileProofOfAddress?.password}
                  />
                }
              />
              <FileDropzone
                info={'Not older than 3 months'}
                name="fileProofOfBankAccount"
                label="Proof of Bank Account"
                defaultValue={submitionFiles && submitionFiles.fileProofOfBankAccount}
                category="proofOfBankDocument"
                tooltip={
                  <Typography>
                    Acceptable proof of bank documents: bank statement (
                    <i>not older than 3 months</i>) or stamped confirmation letter from the bank on
                    a bank letterhead (<i>not older than 3 months</i>)
                  </Typography>
                }
                passwordField={
                  <PasswordInput
                    name={`fileProofOfBankAccount.password`}
                    control={control}
                    label="Password (optional)"
                    defaultValue={submitionFiles?.fileProofOfBankAccount?.password}
                  />
                }
              />
              {altBanking && (
                <>
                  <Typography variant="h5" color="primary" sx={{ mt: 2, mb: 2 }}>
                    Third Party Bank Details
                  </Typography>

                  <Box ml={3}>
                    <Typography sx={{ mt: 1 }} variant="h5">
                      Proof of Identity (Third Party)
                    </Typography>

                    <PrimarySelect
                      name="idTypeThirdParty"
                      control={control}
                      label="Select ID Type"
                      defaultValue={submitionFiles && submitionFiles.idTypeThirdParty}
                      required
                      error={!!errors.idTypeThirdParty}
                      helperText={errors.idTypeThirdParty?.message}
                    >
                      <MenuItem value="document">ID Book</MenuItem>
                      <MenuItem value="card">ID Card</MenuItem>
                      <MenuItem value="passport">Passport</MenuItem>
                    </PrimarySelect>

                    <Typography sx={{ mb: 2 }}>
                      You may scan the document or take a photograph and upload. Please ensure the
                      quality is clear and legible. E.g. South African ID document, Passport, South
                      African ID card. (Please note that if you are uploading a copy/photograph of
                      your South African ID card, we require both the back and front)
                    </Typography>

                    {(watch('idTypeThirdParty') === 'document' ||
                      watch('idTypeThirdParty') === 'passport') && (
                      <FileDropzone
                        name="fileIDDocumentThirdParty"
                        label={watch('idTypeThirdParty') === 'document' ? 'ID Book' : 'Passport'}
                        defaultValue={submitionFiles && submitionFiles.fileIDDocumentThirdParty}
                        category="idDocumentThirdParty"
                        passwordField={
                          <PasswordInput
                            name={`fileIDDocumentThirdParty.password`}
                            control={control}
                            label="Password (optional)"
                            defaultValue={submitionFiles?.fileIDDocumentThirdParty?.password}
                          />
                        }
                      />
                    )}

                    {watch('idTypeThirdParty') === 'card' && (
                      <>
                        <FileDropzone
                          name="fileIDDocumentThirdPartyFront"
                          label="ID Card - Front"
                          defaultValue={
                            submitionFiles && submitionFiles.fileIDDocumentThirdPartyFront
                          }
                          category="idDocumentThirdPartyFront"
                          passwordField={
                            <PasswordInput
                              name={`fileIDDocumentThirdPartyFront.password`}
                              control={control}
                              label="Password (optional)"
                              defaultValue={submitionFiles?.fileIDDocumentThirdPartyFront?.password}
                            />
                          }
                        />
                        <FileDropzone
                          name="fileIDDocumentThirdPartyBack"
                          label="ID Card - Back"
                          defaultValue={
                            submitionFiles && submitionFiles.fileIDDocumentThirdPartyBack
                          }
                          category="idDocumentThirdPartyBack"
                          passwordField={
                            <PasswordInput
                              name={`fileIDDocumentThirdPartyBack.password`}
                              control={control}
                              label="Password (optional)"
                              defaultValue={submitionFiles?.fileIDDocumentThirdPartyBack?.password}
                            />
                          }
                        />
                      </>
                    )}
                  </Box>
                  <FileDropzone
                    info={'Not older than 3 months'}
                    name="fileProofOfAltBankAccount"
                    label="Proof of Third Party Bank Account"
                    defaultValue={submitionFiles && submitionFiles.fileProofOfAltBankAccount}
                    category="proofOfAlternativeBankDocument"
                    tooltip={
                      <Typography>
                        Acceptable proof of bank documents: bank statement (
                        <i>not older than 3 months</i>) or stamped confirmation letter from the bank
                        on a bank letterhead (<i>not older than 3 months</i>)
                      </Typography>
                    }
                    passwordField={
                      <PasswordInput
                        name={`fileProofOfAltBankAccount.password`}
                        control={control}
                        label="Password (optional)"
                        defaultValue={submitionFiles?.fileProofOfAltBankAccount?.password}
                      />
                    }
                  />

                  <FileDropzone
                    name="fileProofOfAddressThirdParty"
                    label="Proof of Address of Third Party"
                    defaultValue={submitionFiles && submitionFiles.fileProofOfAddressThirdParty}
                    category="proofOfAddressThirdParty"
                    passwordField={
                      <PasswordInput
                        name={`fileProofOfAddressThirdParty.password`}
                        control={control}
                        label="Password (optional)"
                        defaultValue={submitionFiles?.fileProofOfAddressThirdParty?.password}
                      />
                    }
                  />
                </>
              )}

              {taxDocRequired && (
                <FileDropzone
                  name="fileTaxDocument"
                  label="Proof of Tax Income Number"
                  defaultValue={submitionFiles && submitionFiles.fileTaxDocument}
                  category="proofOfTaxDocument"
                  passwordField={
                    <PasswordInput
                      name={`fileTaxDocument.password`}
                      control={control}
                      label="Password (optional)"
                      defaultValue={submitionFiles?.fileTaxDocument?.password}
                    />
                  }
                />
              )}
            </>
          )}
        </Form>
      </FormProvider>
      <Box display={'inlineBlock'} mt={3}>
        {objectToArray(errors).map((error, index) => (
          <Typography color={'error'} key={index}>
            {error.value.message}
          </Typography>
        ))}
      </Box>
      <NavButtons isLastStep={false} isFirstStep={false} index={0} />
    </>
  );
}
